import React, { useEffect } from 'react'
import { F0Box, F0Button, F0Icon, F0OneIcon, F0Text } from '@factorialco/f0-react'
import { Ai, Reset } from '@factorialco/f0-react/icons/app'
import { Skeleton } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import {
  trackViewAiSummary,
  trackViewAiResolutionProposal,
  trackGenerateAiSuggestions,
  trackRegenerateAiSuggestions,
} from 'modules/ticketing/lib/tracking'

type TicketAiInsightsProps = {
  aiSummary: string | null | undefined
  aiResolutionProposal: string | null | undefined
  aiSuggestionsMode: string | null | undefined
  aiSuggestionsGeneratedAt: string | null | undefined
  ticketUpdatedAt: string | null | undefined
  onGenerate: () => void
  generating: boolean
}

const isStale = (
  generatedAt: string | null | undefined,
  updatedAt: string | null | undefined
): boolean => {
  if (!generatedAt || !updatedAt) return false
  return new Date(updatedAt).getTime() > new Date(generatedAt).getTime()
}

export const TicketAiInsights: React.FC<TicketAiInsightsProps> = ({
  aiSummary,
  aiResolutionProposal,
  aiSuggestionsMode,
  aiSuggestionsGeneratedAt,
  ticketUpdatedAt,
  onGenerate,
  generating,
}) => {
  const mode = aiSuggestionsMode ?? 'automatic'
  const hasContent = Boolean(aiSummary) || Boolean(aiResolutionProposal)
  const stale = hasContent && isStale(aiSuggestionsGeneratedAt, ticketUpdatedAt)

  const shouldRender = mode !== 'disabled' && !(mode === 'automatic' && !hasContent && !generating)

  useEffect(() => {
    if (!shouldRender || !hasContent) return
    if (aiSummary) trackViewAiSummary()
    if (aiResolutionProposal) trackViewAiResolutionProposal()
  }, [shouldRender, hasContent, aiSummary, aiResolutionProposal])

  const handleGenerate = () => {
    if (hasContent) {
      trackRegenerateAiSuggestions()
    } else {
      trackGenerateAiSuggestions()
    }
    onGenerate()
  }

  if (!shouldRender) return null

  // Loading state — show skeleton continuously until content arrives
  if (generating) {
    return (
      <div className="flex w-full flex-col rounded-lg bg-gradient-to-l from-[#A1ADE51F] via-[#E519431F] to-[#E556191F] p-[1px]">
        <F0Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap="md"
          paddingX="lg"
          paddingY="xs"
        >
          <F0Icon icon={F0OneIcon} size="sm" />
          <F0Text variant="label" content={i18n.t('ticketing.inbox.ai.one_suggestion')} />
        </F0Box>
        <F0Box
          display="flex"
          flexDirection="column"
          borderRadius="xl"
          background="primary"
          paddingX="lg"
          paddingY="lg"
          gap="md"
        >
          <Skeleton width="s40" height="s16" />
          <Skeleton height="s16" />
          <Skeleton height="s16" />
          <Skeleton width="s200" height="s16" />
        </F0Box>
      </div>
    )
  }

  // On-demand mode with no content — show placeholder with generate action in footer
  if (mode === 'on_demand' && !hasContent) {
    return (
      <div className="flex w-full flex-col rounded-lg bg-gradient-to-l from-[#A1ADE51F] via-[#E519431F] to-[#E556191F] p-[1px]">
        <F0Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap="md"
          paddingX="lg"
          paddingY="xs"
        >
          <F0Icon icon={F0OneIcon} size="sm" />
          <F0Text variant="label" content={i18n.t('ticketing.inbox.ai.one_suggestion')} />
        </F0Box>
        <F0Box
          display="flex"
          flexDirection="column"
          borderRadius="xl"
          background="primary"
          paddingX="lg"
          paddingY="lg"
          gap="md"
        >
          <F0Text color="secondary" content={i18n.t('ticketing.inbox.ai.on_demand_placeholder')} />
          <F0Box display="flex" justifyContent="end">
            <F0Button
              variant="outline"
              icon={Ai}
              onClick={handleGenerate}
              label={i18n.t('ticketing.inbox.ai.generate_button')}
            />
          </F0Box>
        </F0Box>
      </div>
    )
  }

  // Content view (automatic or on-demand with content)
  return (
    <div className="flex w-full flex-col rounded-lg bg-gradient-to-l from-[#A1ADE51F] via-[#E519431F] to-[#E556191F] p-[1px]">
      <F0Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="md"
        paddingX="lg"
        paddingY="xs"
      >
        <F0Icon icon={F0OneIcon} size="sm" />
        <F0Text variant="label" content={i18n.t('ticketing.inbox.ai.one_suggestion')} />
      </F0Box>
      <F0Box
        display="flex"
        flexDirection="column"
        borderRadius="xl"
        background="primary"
        paddingX="lg"
        paddingY="lg"
        gap="lg"
      >
        {aiSummary && (
          <F0Box display="flex" flexDirection="column" gap="md">
            <F0Text variant="label" content={i18n.t('ticketing.inbox.ai.summary_title')} />
            <RichTextDisplay
              format="markdown"
              content={aiSummary}
              className="text-md text-f1-foreground-secondary"
            />
          </F0Box>
        )}
        {aiSummary && aiResolutionProposal && <hr className="my-2 border-b-0 border-f1-border" />}
        {aiResolutionProposal && (
          <F0Box display="flex" flexDirection="column" gap="md">
            <F0Text
              variant="label"
              content={i18n.t('ticketing.inbox.ai.resolution_proposal_title')}
            />
            <RichTextDisplay
              format="markdown"
              content={aiResolutionProposal}
              className="text-md text-f1-foreground-secondary"
            />
          </F0Box>
        )}
        {stale && (
          <>
            <hr className="my-2 border-b-0 border-f1-border" />
            <F0Box display="flex" flexDirection="column" alignItems="start" gap="lg">
              <F0Box display="flex" alignItems="center">
                <F0Text variant="small" content={i18n.t('ticketing.inbox.ai.stale_banner')} />
              </F0Box>
              <F0Button
                variant="outline"
                icon={Reset}
                onClick={handleGenerate}
                label={i18n.t('ticketing.inbox.ai.regenerate_button')}
              />
            </F0Box>
          </>
        )}
      </F0Box>
    </div>
  )
}
